<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import type { FormElement, ParagraphProperties } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
  import { useLabelPosition } from '../../../model/useLabelPosition';
  import { useFormBuilderStore } from '../../../model/form-builder.store';
  import { useEditableField } from '../../../model/useFormBuilderReadonly';

  interface Props {
    element: FormElement & { properties: ParagraphProperties };
  }

  const props = defineProps<Props>();
  const formBuilderStore = useFormBuilderStore();

  const inputValue = ref(props.element.properties.value ?? '');

  watch(
    () => props.element.properties.value,
    (newValue) => {
      inputValue.value = newValue ?? '';
    }
  );

  const { containerStyles } = useElementContainerStyles(
    computed(() => props.element.properties),
    'PARAGRAPH'
  );

  const { labelClasses, labelStyles } = useLabelPosition(computed(() => props.element.properties));

  const isEditable = useEditableField(() => props.element.properties.editable);

  const placeholder = computed(() => props.element.properties.placeholder);

  function handleInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    inputValue.value = target.value;

    if (isEditable.value) {
      formBuilderStore.updateCellContentProperty(props.element.id, { value: target.value });
    }
  }
</script>

<template>
  <div
    class="paragraph-element"
    :style="containerStyles"
  >
    <label
      v-if="element.properties.label"
      :for="`paragraph-${element.id}`"
      :class="labelClasses"
      :style="labelStyles"
    >
      {{ element.properties.label }}
    </label>
    <textarea
      :id="`paragraph-${element.id}`"
      :value="inputValue"
      :name="element.properties.name"
      :placeholder="placeholder"
      :required="element.properties.required"
      :aria-label="element.properties.label || element.properties.name"
      :readonly="!isEditable"
      :maxlength="FORM_FIELD_CHARACTER_LIMITS.PARAGRAPH_VALUE"
      :data-log-field-name="element.properties.logFieldName || undefined"
      rows="7"
      class="paragraph-textarea"
      @input="handleInput"
    />
  </div>
</template>

<style scoped>
  .paragraph-element {
    font-family: var(--font-base);
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .paragraph-textarea {
    width: 100%;
    padding: 0.25rem 0.375rem;
    border: 1px solid var(--color-gray-02);
    border-radius: 0.375rem;
    color: var(--color-gray-07);
    background-color: white;
    resize: vertical;
    min-height: 122px;
    font-size: 11px;
    line-height: 16px;
    font-family: inherit;
    transition: border-color 0.15s ease;
  }

  .paragraph-textarea:focus {
    outline: none;
    border-color: var(--theme-secondary-ink);
  }

  .paragraph-textarea::placeholder {
    font-size: 10px;
    opacity: 0.6;
  }
</style>
